import React, { useEffect, forwardRef, useState } from 'react';
import classnames from 'classnames';
import NewsroomFeaturedPrimary from './NewsroomFeaturedPrimary';
import { NewsroomFeaturedArticlesProps, Article } from './types';
import { usePageProps } from "./../../context/PagePropContext";
import { AutomationPlacement, AutomationTestIdElementType, AutomationTestIdFeature, createDataTestId } from '../../utilities/automation';
import HttpService from '../../services/HttpService';
import { Card, CardDeck } from '../../../';
import CardTypeEnum from '../Card/CardTypeEnum';

const NewsroomFeaturedArticles = forwardRef<HTMLDivElement, NewsroomFeaturedArticlesProps>(({
  className,
  editorClassObj,
  cmsId,
}, ref) => {
  const [featuredArticles, setFeaturedArticles] = useState({articles: []});
  const pageProps = usePageProps();
  const articles = pageProps?.newsroom?.featuredArticles || featuredArticles?.articles || [];

  useEffect(() => {
    async function loadFeaturedArticles() {
      const featuredArticlesUrl = `${process.env.GATSBY_API_URL}/newsroom/featured-articles`;
      const featuredArticlesResponse = await HttpService.get<{articles: any[]}>(featuredArticlesUrl, null, {
        params: {
          // bust caching for buildr
          flushCache: cmsId ? Date.now() : null,
        }
      });
      setFeaturedArticles({ articles: featuredArticlesResponse.articles });
    }
    if (!pageProps?.newsroom?.featuredArticles) {
      loadFeaturedArticles();
    }
  }, []);

  if (!articles.length) {
    return null;
  }

  const [primary, ...restArticles] = articles as Article[];
  const featuredSecondaryTestId = createDataTestId({placement: AutomationPlacement.NEWSROOM_ARCHIVE_PAGE, feature: AutomationTestIdFeature.NEWSROOM_FEATURED, element: AutomationTestIdElementType.COLLECTION, specifier: 'SecondaryFeature'})
  const featuredSecondaryItemTestId = createDataTestId({placement: AutomationPlacement.NEWSROOM_ARCHIVE_PAGE, feature: AutomationTestIdFeature.NEWSROOM_FEATURED, element: AutomationTestIdElementType.COLLECTION_ITEM, specifier: 'SecondaryFeature'});

  return (
    <div data-id={cmsId} ref={ref} className={classnames('container content pt-6 bg-gray-100 border-0', className, editorClassObj)}>
      <div className="row">
        <div className="px-xl-6 px-lg-5 px-4 col-lg">
          <h1 className="mb-6" style={{fontWeight: 700}}>Newsroom</h1>
        </div>
      </div>
      {primary && <NewsroomFeaturedPrimary article={primary} />}
      <div className="row">
        <div className="px-xl-6 px-lg-5 px-4 py-6 col-lg">
          <CardDeck
            dataTestId={featuredSecondaryTestId}
            enableSwipe={false}
            enableAccordion={false}
            enableArrows={false}
            enableCounter={false}
            desktopColumns={3}
            tabletColumns={2}
            mobileColumns={1}
            enableOverflow={false}
            cardClassName='mb-3 mb-lg-0'
            cardType={CardTypeEnum.SimpleTextPhoto}>
              {restArticles.map(article => {
                return (<Card
                key={article.path}
                dataTestId={featuredSecondaryItemTestId}
                cardAlt={article.imageAlt}
                cardImage={article.imageSrc}
                cardTitle={article.title}
                href={article.path} />);
              })}
          </CardDeck>
        </div>
      </div>
    </div>
  );
});

export default NewsroomFeaturedArticles;
